/* eslint-disable */
// Contents of an "Assembled At Outlet" box (Product.freshlyPrepared) on a Buyer SO line.
// Shows what goes inside one box - the box's standard recipe, or the list the user changed it
// to - with an edit button that opens a modal to replace items, change quantities and add items.
//
// A changed list rides on the order line as extraData.content = [{variantId, qtyPerBox, ...}]
// (per box, in the item's base unit); console2.0_be assembly_helpers._recipeFromExternalContent
// stages it instead of the recipe when the box's production orders are raised. No content means
// the standard recipe. extraData.contentVariantId records which box variant the list was made
// for, so changing the line's variant drops a list that no longer applies.
import { useEffect, useMemo, useState } from 'react';
import {
	Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import { Camera, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { assemblyServices, notificationServices } from '@/Services';

// One request per box variant per page load.
const recipeCache = new Map();

const fetchRecipe = (variantId) => {
	if (!recipeCache.has(variantId)) {
		const request = assemblyServices.fetchStandardRecipe(variantId)
			.then((res) => res?.data?.recipe ?? [])
			.catch((err) => {
				recipeCache.delete(variantId);
				throw err;
			});
		recipeCache.set(variantId, request);
	}
	return recipeCache.get(variantId);
};

export const unitLabel = (unit) => {
	if (unit === 'gram') return 'g';
	if (unit === 'count') return 'pcs';
	return unit ?? '';
};

// Several products can share a name (e.g. three "Kaju Badam Mixture"), so the variant name and
// item code are shown too - the variant name only when it says more than the code itself.
const optionLabel = (p, v) => {
	const code = v?.clientVariantId ? String(v.clientVariantId) : '';
	const display = v?.displayName ? String(v.displayName).trim() : '';
	const displayIsCode = display && code && display.replace(/^0+/, '') === code.replace(/^0+/, '');
	const name = display && !displayIsCode ? `${p?.productName} - ${display}` : p?.productName;
	return code ? `${name} (${code})` : name;
};

const fmtQty = (qty) => Number(Math.round(Number(qty) * 1000) / 1000);

// "75 g (10 Pcs)" - pieces shown for a gram item whose per-piece weight is known.
export const qtyText = (qty, unit, perPieceWeight) => {
	const base = `${fmtQty(qty)} ${unitLabel(unit)}`;
	const weight = Number(perPieceWeight);
	if (unit !== 'gram' || !weight || weight <= 0) return base;
	const pcs = Math.round((Number(qty) / weight) * 100) / 100;
	return `${base} (${pcs} Pcs)`;
};

const toContent = (recipe) => (recipe ?? []).map((c) => ({
	variantId: Number(c?.variantId),
	productName: c?.productName,
	unit: c?.unit,
	qtyPerBox: fmtQty(c?.qtyPerBox),
	perPieceWeight: c?.perPieceWeight ?? null,
	skuType: c?.skuType ?? null,
	clientVariantId: c?.clientVariantId ?? null,
}));

const sameContent = (a, b) => {
	if ((a?.length ?? 0) !== (b?.length ?? 0)) return false;
	const key = (list) => list
		.map((c) => `${Number(c?.variantId)}:${fmtQty(c?.qtyPerBox)}`)
		.sort()
		.join('|');
	return key(a) === key(b);
};

let rowKeySeed = 0;
const nextKey = () => {
	rowKeySeed += 1;
	return `row-${rowKeySeed}`;
};

// A gram item with a per-piece weight is entered in pieces; grams are still what's saved.
const inPieces = (option) => option?.unit === 'gram' && Number(option?.perPieceWeight) > 0;

// Grams -> what the quantity box shows for this item.
const toInput = (grams, option) => {
	if (grams === '' || grams === null || grams === undefined) return '';
	return String(inPieces(option)
		? fmtQty(Number(grams) / Number(option.perPieceWeight))
		: fmtQty(grams));
};

// What the quantity box holds -> grams (or the item's own unit).
const toGrams = (input, option) => (inPieces(option)
	? Number(input) * Number(option.perPieceWeight)
	: Number(input));

const seedRow = (c) => ({
	key: nextKey(),
	original: c,
	variantId: c?.variantId,
	qtyInput: toInput(c?.qtyPerBox, c),
});

const BoxContentsModal = ({
	open, onClose, onSave, contents, standard, itemOptions, boxName, boxCode,
}) => {
	const [rows, setRows] = useState([]);

	useEffect(() => {
		if (!open) return;
		setRows((contents ?? []).map(seedRow));
	}, [open, contents]);

	const optionById = useMemo(() => {
		const map = new Map();
		itemOptions.forEach((o) => map.set(Number(o.variantId), o));
		return map;
	}, [itemOptions]);

	// An item already in the box can't be picked again on another row.
	const usedIds = useMemo(
		() => new Set(rows.map((r) => Number(r.variantId)).filter(Boolean)),
		[rows]
	);

	const updateRow = (key, patch) => {
		setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
	};

	const resolveOption = (row) => {
		if (!row?.variantId) return null;
		return optionById.get(Number(row.variantId)) ?? {
			variantId: Number(row.variantId),
			label: row?.original?.productName ?? `Item ${row.variantId}`,
			unit: row?.original?.unit,
			perPieceWeight: row?.original?.perPieceWeight,
			skuType: row?.original?.skuType,
			clientVariantId: row?.original?.clientVariantId,
		};
	};

	// Save needs at least two PRODUCT items; at most one PACKAGING item may be added.
	const productCount = rows.filter((r) => resolveOption(r)?.skuType === 'PRODUCT').length;
	const packagingRowKey = rows.find((r) => resolveOption(r)?.skuType === 'PACKAGING')?.key;
	const canSave = productCount >= 2;

	const handleSave = () => {
		if (!canSave) return;
		const content = [];
		for (const row of rows) {
			const option = resolveOption(row);
			if (!option) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Select an item on every row',
				});
				return;
			}
			const qty = toGrams(row.qtyInput, option);
			if (!row.qtyInput || Number.isNaN(qty) || qty <= 0) {
				notificationServices.generateNotification({
					type: 'error',
					message: `Enter a quantity greater than zero for ${option.label}`,
				});
				return;
			}
			content.push({
				variantId: Number(option.variantId),
				productName: option.productName ?? option.label,
				skuType: option.skuType ?? null,
				clientVariantId: option.clientVariantId ?? null,
				unit: option.unit,
				qtyPerBox: fmtQty(qty),
				perPieceWeight: option.perPieceWeight ?? null,
			});
		}
		onSave(content);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
			<DialogTitle>
				Box contents - {boxName}
				{boxCode && <span className='text-gray-500'> ({boxCode})</span>}
				<p className='m-0 text-[13px] font-normal text-gray-500'>
					Quantities are per box. Replace an item, change its quantity, or add items.
				</p>
			</DialogTitle>
			<DialogContent dividers>
				<div className='grid grid-cols-[1fr_1.3fr_170px_40px] items-center gap-3 pb-2 text-[12px] font-semibold uppercase text-gray-500'>
					<span>Current item</span>
					<span>New item</span>
					<span>Qty per box</span>
					<span />
				</div>
				{rows.map((row) => {
					const option = resolveOption(row);
					return (
						<div
							key={row.key}
							className='grid grid-cols-[1fr_1.3fr_170px_40px] items-center gap-3 border-t border-gray-100 py-2'
						>
							<span className='text-[14px] text-gray-800'>
								{row.original ? (
									<>
										{row.original.productName}
										{row.original.clientVariantId && (
											<span className='text-gray-500'> ({row.original.clientVariantId})</span>
										)}
										<span className='block text-[12px] text-gray-500'>
											{qtyText(row.original.qtyPerBox, row.original.unit, row.original.perPieceWeight)}
										</span>
									</>
								) : <span className='font-medium text-teal-700'>New item</span>}
							</span>
							<Autocomplete
								size='small'
								// Items already in the box on another row are left out; the row's own
								// item stays so its current value still shows.
								// Packaging is left out once another row holds the one allowed.
								options={itemOptions.filter((o) => Number(o.variantId) === Number(row.variantId)
									|| (!usedIds.has(Number(o.variantId))
										&& !(o.skuType === 'PACKAGING' && packagingRowKey && packagingRowKey !== row.key)))}
								value={option}
								getOptionLabel={(o) => (o?.skuType === 'PACKAGING' ? `${o?.label ?? ''} (Packaging)` : o?.label ?? '')}
								// Keyed by variant: labels can still repeat (same name, code and variant).
								renderOption={(props, o) => (
									<li {...props} key={o.variantId}>
										{o?.skuType === 'PACKAGING' ? `${o?.label ?? ''} (Packaging)` : o?.label}
									</li>
								)}
								isOptionEqualToValue={(o, v) => Number(o?.variantId) === Number(v?.variantId)}
								onChange={(e, value) => {
									// Keep the weight when the item changes; show it in the new item's terms.
									const grams = row.qtyInput === '' ? '' : toGrams(row.qtyInput, option);
									updateRow(row.key, {
										variantId: value?.variantId ?? null,
										qtyInput: toInput(grams, value),
									});
								}}
								renderInput={(params) => (
									<TextField {...params} placeholder='Search item' />
								)}
							/>
							<TextField
								size='small'
								type='number'
								// Pieces for a gram item with a per-piece weight - the weight is shown
								// underneath for reference.
								helperText={inPieces(option)
									? (Number(row.qtyInput) > 0
										? `= ${fmtQty(toGrams(row.qtyInput, option))} g (${fmtQty(option.perPieceWeight)} g/pc)`
										: `${fmtQty(option.perPieceWeight)} g per piece`)
									: undefined}
								value={row.qtyInput}
								inputProps={{ min: 0, step: 'any' }}
								onChange={(e) => updateRow(row.key, { qtyInput: e.target.value })}
								InputProps={{
									endAdornment: (
										<span className='pl-1 text-[12px] text-gray-500'>
											{inPieces(option) ? 'Pcs' : unitLabel(option?.unit)}
										</span>
									),
								}}
							/>
							<button
								type='button'
								title='Remove item'
								onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
								className='flex items-center justify-center rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600'
							>
								<Trash2 size={16} />
							</button>
						</div>
					);
				})}
				<button
					type='button'
					onClick={() => setRows((prev) => [
						...prev,
						{ key: nextKey(), original: null, variantId: null, qtyInput: '' },
					])}
					className='mt-3 flex items-center gap-1.5 rounded-md border border-dashed border-teal-600 px-3 py-1.5 text-[13px] font-medium text-teal-700 hover:bg-teal-50'
				>
					<Plus size={14} />
					Add item
				</button>
			</DialogContent>
			<DialogActions className='justify-between'>
				<div className='flex gap-1'>
					<Button
						startIcon={<RotateCcw size={14} />}
						disabled={!standard?.length}
						onClick={() => setRows(standard.map(seedRow))}
					>
						Reset to standard recipe
					</Button>
					<Button
						color='error'
						startIcon={<Trash2 size={14} />}
						disabled={rows.length === 0}
						onClick={() => setRows([])}
					>
						Remove all items
					</Button>
				</div>
				<div className='flex items-center gap-2'>
					{!canSave && (
						<span className='text-[12px] text-gray-500'>
							Add at least 2 products to save
						</span>
					)}
					<Button onClick={onClose}>Cancel</Button>
					<Button variant='contained' onClick={handleSave} disabled={!canSave}>Save</Button>
				</div>
			</DialogActions>
		</Dialog>
	);
};

// A photo shrunk to at most PHOTO_MAX_SIDE px as a JPEG data: URI - phone camera photos are
// several MB, and they travel inside the order request.
const PHOTO_MAX_SIDE = 1600;
const shrinkPhoto = (file) => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.onerror = () => reject(new Error('Could not read the photo'));
	reader.onload = () => {
		const img = new Image();
		img.onerror = () => reject(new Error('This file is not an image'));
		img.onload = () => {
			const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(img.width, img.height));
			const canvas = document.createElement('canvas');
			canvas.width = Math.round(img.width * scale);
			canvas.height = Math.round(img.height * scale);
			canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
			resolve(canvas.toDataURL('image/jpeg', 0.8));
		};
		img.src = reader.result;
	};
	reader.readAsDataURL(file);
});

// Photos of an edited box line (optional) - saved on the order line once the order is placed
// (console2.0_be purchase_order._takeItemImages). Kept on the row as extraData.images.
const BoxPhotos = ({ images = [], disabled, onChange }) => {
	const handleFiles = async (event) => {
		const files = Array.from(event.target.files || []);
		event.target.value = '';
		if (!files.length) return;
		try {
			const added = await Promise.all(files.map(shrinkPhoto));
			onChange([...images, ...added]);
		} catch (err) {
			notificationServices.generateNotification({
				type: 'error',
				message: err?.message || 'Could not add the photo',
			});
		}
	};

	return (
		<div className='mt-1.5 border-t border-amber-200 pt-1.5'>
			<span className='text-[11px] font-semibold uppercase text-amber-800'>
				Photos (optional)
			</span>
			<div className='mt-1 flex flex-wrap items-center gap-1.5'>
				{images.map((src, idx) => (
					<div key={idx} className='relative h-12 w-12 overflow-hidden rounded border border-amber-200'>
						<img src={src} alt={`Box photo ${idx + 1}`} className='h-full w-full object-cover' />
						{!disabled && (
							<button
								type='button'
								title='Remove photo'
								onClick={() => onChange(images.filter((_, i) => i !== idx))}
								className='absolute right-0 top-0 rounded-bl bg-black/60 p-0.5 text-white'
							>
								<X size={10} />
							</button>
						)}
					</div>
				))}
				{!disabled && (
					<label
						title='Capture or upload a photo of this box'
						className='flex h-12 w-12 cursor-pointer flex-col items-center justify-center rounded border border-dashed border-amber-400 text-amber-800 hover:bg-amber-100'
					>
						<Camera size={16} />
						<span className='text-[9px] font-medium'>Add</span>
						<input
							type='file'
							accept='image/*'
							capture='environment'
							multiple
							className='hidden'
							onChange={handleFiles}
						/>
					</label>
				)}
			</div>
		</div>
	);
};

const BoxContents = ({ product, productsList = [], disabled, onChange, onImagesChange = () => {} }) => {
	const variantId = product?.selectedVariant?.id;
	const custom = product?.extraData?.content;
	const customFor = product?.extraData?.contentVariantId;
	const [standard, setStandard] = useState(null);
	const [error, setError] = useState(null);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!variantId) return undefined;
		let alive = true;
		setStandard(null);
		setError(null);
		fetchRecipe(variantId)
			.then((recipe) => alive && setStandard(toContent(recipe)))
			.catch((err) => alive && setError(typeof err === 'string' ? err : 'Could not load the box contents'));
		return () => {
			alive = false;
		};
	}, [variantId]);

	// A list made for another variant of this line no longer applies.
	useEffect(() => {
		if (custom && customFor && variantId && Number(customFor) !== Number(variantId)) {
			onChange(product?.id, null, null);
		}
	}, [variantId]);

	const itemOptions = useMemo(() => {
		const options = [];
		(productsList ?? []).forEach((p) => {
			// Finished products go into a box, plus one packaging item; never the box itself.
			if (!['PRODUCT', 'PACKAGING'].includes(p?.skuType)) return;
			if (Number(p?.id) === Number(product?.productId)) return;
			const variants = p?.variants ?? [];
			variants.forEach((v) => {
				if (Number(v?.id) === Number(variantId)) return; // a box can't contain itself
				options.push({
					variantId: Number(v?.id),
					productName: p?.productName,
					label: optionLabel(p, v),
					unit: p?.unit,
					perPieceWeight: p?.perPieceWeight ?? null,
					skuType: p?.skuType,
					clientVariantId: v?.clientVariantId ?? null,
				});
			});
		});
		return options;
	}, [productsList, variantId, product?.productId]);

	const perPieceByVariant = useMemo(() => {
		const map = new Map();
		itemOptions.forEach((o) => map.set(o.variantId, o.perPieceWeight));
		return map;
	}, [itemOptions]);

	const codeByVariant = useMemo(() => {
		const map = new Map();
		(productsList ?? []).forEach((p) => (p?.variants ?? []).forEach((v) => {
			if (v?.clientVariantId) map.set(Number(v.id), v.clientVariantId);
		}));
		return map;
	}, [productsList]);

	const isCustom = Array.isArray(custom) && custom.length > 0;
	// Memoised: the modal re-seeds its rows whenever this list changes identity.
	const contents = useMemo(() => (isCustom ? custom : standard)?.map((c) => ({
		...c,
		perPieceWeight: c?.perPieceWeight ?? perPieceByVariant.get(Number(c?.variantId)) ?? null,
		clientVariantId: c?.clientVariantId ?? codeByVariant.get(Number(c?.variantId)) ?? null,
	})), [isCustom, custom, standard, perPieceByVariant, codeByVariant]);

	const handleSave = (content) => {
		setOpen(false);
		// Back to the standard recipe: send nothing, the server stages the recipe itself.
		if (standard && sameContent(content, standard)) {
			onChange(product?.id, null, null);
			return;
		}
		onChange(product?.id, content, variantId);
	};

	if (!variantId) return null;

	return (
		<div className='mt-1 w-full rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-left'>
			<div className='flex items-center justify-between gap-2'>
				<span className='text-[11px] font-semibold uppercase text-amber-800'>
					Box contents (per box)
					{isCustom && (
						<span className='ml-1.5 rounded bg-amber-200 px-1 py-px text-[10px] text-amber-900'>
							Edited
						</span>
					)}
				</span>
				{!disabled && (contents || error) && (
					<button
						type='button'
						title='Change box contents'
						onClick={() => setOpen(true)}
						className='flex items-center rounded p-0.5 text-amber-800 hover:bg-amber-100'
					>
						<Pencil size={13} />
					</button>
				)}
			</div>
			{!contents && !error && (
				<p className='m-0 text-[12px] text-gray-500'>Loading contents...</p>
			)}
			{!isCustom && error && (
				<p className='m-0 text-[12px] text-red-600'>{error}</p>
			)}
			{contents?.map((c) => (
				<div key={c.variantId} className='flex justify-between gap-2 text-[12px] text-gray-800'>
					<span className='truncate'>
						{c.productName}
						{c.skuType === 'PACKAGING' && <span className='text-gray-500'> (Packaging)</span>}
					</span>
					<span className='whitespace-nowrap'>
						{qtyText(c.qtyPerBox, c.unit, c.perPieceWeight)}
					</span>
				</div>
			))}
			<BoxContentsModal
				open={open}
				onClose={() => setOpen(false)}
				onSave={handleSave}
				contents={contents ?? []}
				standard={standard}
				itemOptions={itemOptions}
				boxName={product?.productName}
				boxCode={product?.selectedVariant?.clientVariantId}
			/>
			{isCustom && (
				<BoxPhotos
					images={product?.extraData?.images ?? []}
					disabled={disabled}
					onChange={(images) => onImagesChange(product?.id, images)}
				/>
			)}
		</div>
	);
};

export default BoxContents;
